import { defineStore } from 'pinia'

export type ToastType = 'success' | 'info' | 'warning' | 'error' | 'progress'

export interface ToastAction {
  label: string
  handler?: () => void | Promise<void>
}

export interface NotificationContext {
  toolId?: number | string
  scope?: string
  correlationId?: string
}

export interface NotificationToast {
  id: string
  type: ToastType
  title?: string
  message?: string
  duration?: number
  persistent?: boolean
  dismissible?: boolean
  action?: ToastAction
  progress?: number
  icon?: string
  context?: NotificationContext
  createdAt: number
}

export interface ConfirmDialogDetail {
  label: string
  value: string
}

export type ConfirmDialogType = 'danger' | 'warning' | 'info' | 'neutral'

export interface ConfirmDialogInput {
  title: string
  message: string
  type?: ConfirmDialogType
  details?: ConfirmDialogDetail[]
  confirmLabel?: string
  cancelLabel?: string
  checkboxLabel?: string
  requiresAcknowledgement?: boolean
  context?: NotificationContext
}

export interface ConfirmDialogState extends Required<Omit<ConfirmDialogInput, 'checkboxLabel' | 'requiresAcknowledgement' | 'type' | 'context'>> {
  id: string
  type: ConfirmDialogType
  checkboxLabel?: string
  requiresAcknowledgement: boolean
  acknowledged: boolean
  busy: boolean
  context?: NotificationContext
  resolve?: (confirmed: boolean) => void
}

export type NotificationToastInput = Omit<NotificationToast, 'id' | 'createdAt'> & { id?: string }

let toastCounter = 0
let dialogCounter = 0
const timeoutHandles = new Map<string, number>()

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    toasts: [] as NotificationToast[],
    dialog: null as ConfirmDialogState | null
  }),
  actions: {
    pushToast(toast: NotificationToastInput): string {
      const id = toast.id || `toast-${++toastCounter}`
      const duration = toast.duration ?? (toast.type === 'error' ? 6000 : 4000)
      const newToast: NotificationToast = {
        id,
        type: toast.type,
        title: toast.title,
        message: toast.message,
        duration,
        persistent: toast.persistent ?? false,
        dismissible: toast.dismissible ?? true,
        action: toast.action,
        progress: toast.progress,
        icon: toast.icon,
        context: toast.context,
        createdAt: Date.now()
      }

      const existingIndex = this.toasts.findIndex(item => item.id === id)
      if (existingIndex > -1) {
        this.toasts.splice(existingIndex, 1, newToast)
      } else {
        this.toasts.push(newToast)
      }

      this.scheduleAutoDismiss(newToast)
      return id
    },

    updateToast(id: string, patch: Partial<Omit<NotificationToast, 'id' | 'createdAt'>>): void {
      const index = this.toasts.findIndex(toast => toast.id === id)
      if (index === -1) return

      const updated: NotificationToast = {
        ...this.toasts[index],
        ...patch
      }

      this.toasts.splice(index, 1, updated)
      this.scheduleAutoDismiss(updated)
    },

    dismissToast(id: string): void {
      const index = this.toasts.findIndex(toast => toast.id === id)
      if (index === -1) return

      this.toasts.splice(index, 1)
      this.clearTimeout(id)
    },

    clearToasts(): void {
      this.toasts.forEach(toast => this.clearTimeout(toast.id))
      this.toasts = []
    },

    scheduleAutoDismiss(toast: NotificationToast): void {
      this.clearTimeout(toast.id)

      if (toast.persistent || !toast.duration || toast.duration <= 0) {
        return
      }

      const handle = window.setTimeout(() => {
        this.dismissToast(toast.id)
      }, toast.duration)

      timeoutHandles.set(toast.id, handle)
    },

    clearTimeout(id: string): void {
      const handle = timeoutHandles.get(id)
      if (handle) {
        window.clearTimeout(handle)
        timeoutHandles.delete(id)
      }
    },

    showDialog(options: ConfirmDialogInput): Promise<boolean> {
      return new Promise(resolve => {
        if (this.dialog?.resolve) {
          this.dialog.resolve(false)
        }

        const id = `dialog-${++dialogCounter}`
        const dialogState: ConfirmDialogState = {
          id,
          type: options.type ?? 'info',
          title: options.title,
          message: options.message,
          details: options.details ?? [],
          confirmLabel: options.confirmLabel ?? 'Bestätigen',
          cancelLabel: options.cancelLabel ?? 'Abbrechen',
          checkboxLabel: options.checkboxLabel,
          requiresAcknowledgement: options.requiresAcknowledgement ?? false,
          acknowledged: !(options.requiresAcknowledgement ?? false),
          busy: false,
          context: options.context,
          resolve
        }

        this.dialog = dialogState
      })
    },

    setDialogAcknowledged(value: boolean): void {
      if (!this.dialog) return
      this.dialog = { ...this.dialog, acknowledged: value }
    },

    setDialogBusy(value: boolean): void {
      if (!this.dialog) return
      this.dialog = { ...this.dialog, busy: value }
    },

    resolveDialog(confirmed: boolean): boolean {
      if (!this.dialog) {
        return false
      }

      if (confirmed && this.dialog.requiresAcknowledgement && !this.dialog.acknowledged) {
        return false
      }

      const currentDialog = this.dialog
      this.dialog = null
      currentDialog.resolve?.(confirmed)
      return confirmed
    }
  }
})
